import React, { useEffect, useState, useCallback } from "react";
import { Loader2, Plus, MapPin, Navigation, MessageCircle, Phone, Printer, Trash2, Pencil, ChevronUp, ChevronDown, AlertTriangle, CheckCircle2, Truck, PackageCheck, Clock, Camera, X, CalendarDays, ListChecks, Lock, Send } from "lucide-react";
import api, { formatApiError, openPdf } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";
import { GT_BANKS } from "../lib/banks";
import { TaskDocAttach } from "../components/TaskDocAttach";

const STATUS_STYLE = {
  "Pendiente": "bg-slate-100 text-slate-600", "En camino": "bg-blue-100 text-blue-700",
  "En proceso": "bg-amber-100 text-amber-700", "Realizada": "bg-green-100 text-green-700",
  "Problema": "bg-red-100 text-red-700",
};
const TYPE_STYLE = {
  "Entrega": "bg-primary/10 text-primary", "Recogida": "bg-indigo-100 text-indigo-700",
  "Cobro": "bg-emerald-100 text-emerald-700", "Trámite": "bg-purple-100 text-purple-700", "Otro": "bg-slate-100 text-slate-600",
};
const wazeLink = (a) => `https://waze.com/ul?q=${encodeURIComponent(a || "")}&navigate=yes`;
const mapsLink = (a) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(a || "")}`;
const waLink = (n) => `https://wa.me/${(n || "").replace(/\D/g, "")}`;
const fileUrl = (id) => `${process.env.REACT_APP_BACKEND_URL}/api/files/${id}?auth=${localStorage.getItem("selec_token")}`;
const toIsoDate = (ddmmyyyy) => { if (!ddmmyyyy) return ""; const p = ddmmyyyy.split("/"); if (p.length < 3) return ""; const [d, m, y] = p; return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`; };
const toDdmmyyyy = (iso) => { if (!iso) return ""; const p = iso.split("-"); if (p.length < 3) return ""; const [y, m, d] = p; return `${d}/${m}/${y}`; };
const fmtDateTime = (iso) => { if (!iso) return ""; try { return new Date(iso).toLocaleString("es-GT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };

function StatCard({ label, value, color, testId }) {
  return (
    <Card className="p-4 flex flex-col gap-1" data-testid={testId}>
      <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{label}</span>
      <span className={`text-3xl font-heading font-black ${color}`}>{value}</span>
    </Card>
  );
}

export default function RoutesPage() {
  const { can } = useAuth();
  const manage = can("routes:manage");
  const [meta, setMeta] = useState({ route_shifts: [], task_types: [], task_statuses: [], problem_types: [] });
  const [messengers, setMessengers] = useState([]);
  const [tab, setTab] = useState("hoy");

  useEffect(() => {
    api.get("/logistics/meta").then((r) => setMeta(r.data)).catch(() => {});
    if (manage) api.get("/logistics/messengers?role=mensajero").then((r) => setMessengers(r.data)).catch(() => {});
  }, [manage]);

  return (
    <div className="space-y-5">
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="routes-tabs">
          <TabsTrigger value="hoy" data-testid="tab-hoy">Hoy</TabsTrigger>
          <TabsTrigger value="rutas" data-testid="tab-rutas">Rutas</TabsTrigger>
          <TabsTrigger value="historial" data-testid="tab-historial">Historial</TabsTrigger>
        </TabsList>
        <TabsContent value="hoy" className="mt-4"><TodayView meta={meta} manage={manage} /></TabsContent>
        <TabsContent value="rutas" className="mt-4"><RoutesList meta={meta} manage={manage} messengers={messengers} history={false} /></TabsContent>
        <TabsContent value="historial" className="mt-4"><RoutesList meta={meta} manage={manage} messengers={messengers} history={true} /></TabsContent>
      </Tabs>
    </div>
  );
}

function TodayView({ meta, manage }) {
  const [data, setData] = useState(null);
  const [openRoute, setOpenRoute] = useState(null);
  const load = useCallback(() => { api.get("/routes/today").then((r) => setData(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  const c = data.counts;
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard label="Tareas hoy" value={c.total} color="text-foreground" testId="today-total" />
        <StatCard label="Realizadas" value={c.done} color="text-green-600" testId="today-done" />
        <StatCard label="Pendientes" value={c.pending} color="text-amber-600" testId="today-pending" />
        <StatCard label="Con problema" value={c.problem} color="text-red-600" testId="today-problem" />
      </div>
      {data.next_task && (
        <Card className="p-4 border-l-4 border-l-primary" data-testid="today-next">
          <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-1">Próxima tarea</p>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div><p className="font-heading font-bold">#{data.next_task.order} · {data.next_task.place}</p>
              <p className="text-sm text-muted-foreground">{data.next_task.address || "Sin dirección"}</p></div>
            <a href={wazeLink(data.next_task.address)} target="_blank" rel="noreferrer">
              <Button size="sm" className="gap-1.5" data-testid="today-next-waze"><Navigation className="h-4 w-4" /> Waze</Button></a>
          </div>
        </Card>
      )}
      <div>
        <h3 className="font-heading font-bold mb-2">Rutas de hoy</h3>
        {data.routes.length === 0 ? <p className="text-sm text-muted-foreground py-6 text-center">No tienes rutas asignadas hoy.</p> : (
          <div className="grid gap-3 sm:grid-cols-2">
            {data.routes.map((r) => <RouteCard key={r.id} route={r} onOpen={() => setOpenRoute(r.id)} />)}
          </div>
        )}
      </div>
      {openRoute && <RouteDetailDialog rid={openRoute} meta={meta} manage={manage} onClose={() => { setOpenRoute(null); load(); }} />}
    </div>
  );
}

function RouteCard({ route, onOpen }) {
  const c = route.counts || { total: 0, done: 0, pending: 0, problem: 0 };
  return (
    <Card className="p-4 cursor-pointer hover:shadow-elevated transition-shadow" onClick={onOpen} data-testid={`route-card-${route.number}`}>
      <div className="flex items-center justify-between">
        <span className="font-mono text-xs font-bold text-primary">{route.number}</span>
        <Badge className={STATUS_STYLE[route.status] || "bg-slate-100 text-slate-600"}>{route.status}</Badge>
      </div>
      <p className="font-heading font-bold mt-1">{route.name} <span className="text-muted-foreground font-normal">· {route.shift}</span></p>
      <p className="text-sm text-muted-foreground">{route.messenger_name} · {route.date}{route.flexibility ? ` · ${route.flexibility}` : ""}</p>
      {!route.published && <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-primary bg-primary/10 rounded px-1.5 py-0.5 mt-1" data-testid={`route-draft-badge-${route.number}`}>Borrador</span>}
      {route.received_at && <p className="text-[11px] text-green-700 font-semibold flex items-center gap-1 mt-1" data-testid={`route-received-card-${route.number}`}><CheckCircle2 className="h-3 w-3" /> Recibida por {route.received_by_name} · {fmtDateTime(route.received_at)}</p>}
      <div className="flex gap-3 mt-2 text-xs">
        <span className="text-green-600 font-bold">{c.done} ✓</span>
        <span className="text-amber-600 font-bold">{c.pending} pend.</span>
        {c.problem > 0 && <span className="text-red-600 font-bold">{c.problem} prob.</span>}
        <span className="text-muted-foreground ml-auto">{c.total} tareas</span>
      </div>
    </Card>
  );
}

function RoutesList({ meta, manage, messengers, history }) {
  const [routes, setRoutes] = useState(null);
  const [openRoute, setOpenRoute] = useState(null);
  const [form, setForm] = useState(null);
  const [df, setDf] = useState("");
  const [dt, setDt] = useState("");

  const load = useCallback(() => {
    const p = new URLSearchParams();
    // "Rutas": solo hoy y días siguientes (sin días anteriores). "Historial": todo, con filtro de fechas.
    const from = history ? df : (df || new Date(Date.now()-216e5).toISOString().slice(0, 10));
    if (from) p.set("date_from", from);
    if (dt) p.set("date_to", dt);
    api.get(`/routes${p.toString() ? `?${p}` : ""}`).then((r) => setRoutes(r.data)).catch(() => {});
  }, [df, dt, history]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.messenger_id) return toast.error("Selecciona el mensajero");
    try {
      const payload = { ...form, date: toDdmmyyyy(form.date) };
      if (form.id) await api.put(`/routes/${form.id}`, payload);
      else await api.post("/routes", payload);
      toast.success("Ruta guardada"); setForm(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (r) => { if (!window.confirm(`¿Eliminar ${r.number} y sus tareas?`)) return; try { await api.delete(`/routes/${r.id}`); toast.success("Ruta eliminada"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  if (!routes) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <div className="flex items-center gap-2">
          {history && <><Input type="date" value={df} onChange={(e) => setDf(e.target.value)} className="h-9 w-40 text-xs" data-testid="route-from" />
            <span className="text-xs text-muted-foreground">a</span>
            <Input type="date" value={dt} onChange={(e) => setDt(e.target.value)} className="h-9 w-40 text-xs" data-testid="route-to" /></>}
        </div>
        {manage && !history && <Button className="gap-2" onClick={() => setForm({ messenger_id: "", name: "Ruta", shift: "Mañana", flexibility: "Flexible", date: "", notes: "" })} data-testid="new-route-btn"><Plus className="h-4 w-4" /> Nueva ruta</Button>}
      </div>
      {routes.length === 0 ? <p className="text-sm text-muted-foreground py-10 text-center">Sin rutas.</p> : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {routes.map((r) => (
            <div key={r.id} className="relative group">
              <RouteCard route={r} onOpen={() => setOpenRoute(r.id)} />
              <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); openPdf(`/routes/${r.id}/pdf?detailed=true`); }} data-testid={`route-pdf-${r.number}`}><Printer className="h-3.5 w-3.5" /></Button>
                {manage && <><Button size="icon" variant="ghost" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); setForm({ id: r.id, messenger_id: r.messenger_id, name: r.name, shift: r.shift, flexibility: r.flexibility || "Flexible", date: toIsoDate(r.date), notes: r.notes || "" }); }} data-testid={`route-edit-${r.number}`}><Pencil className="h-3.5 w-3.5" /></Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={(e) => { e.stopPropagation(); del(r); }} data-testid={`route-del-${r.number}`}><Trash2 className="h-3.5 w-3.5" /></Button></>}
              </div>
            </div>
          ))}
        </div>
      )}
      {openRoute && <RouteDetailDialog rid={openRoute} meta={meta} manage={manage} onClose={() => { setOpenRoute(null); load(); }} />}
      {form && (
        <Dialog open onOpenChange={() => setForm(null)}>
          <DialogContent data-testid="route-form-dialog">
            <DialogHeader><DialogTitle>{form.id ? "Editar ruta" : "Nueva ruta"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label className="text-xs">Mensajero</Label>
                <Select value={form.messenger_id} onValueChange={(v) => setForm({ ...form, messenger_id: v })}>
                  <SelectTrigger data-testid="route-messenger"><SelectValue placeholder="Selecciona" /></SelectTrigger>
                  <SelectContent>{messengers.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent></Select></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Nombre</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="route-name" /></div>
                <div><Label className="text-xs">Jornada</Label>
                  <Select value={form.shift} onValueChange={(v) => setForm({ ...form, shift: v })}>
                    <SelectTrigger data-testid="route-shift"><SelectValue /></SelectTrigger>
                    <SelectContent>{meta.route_shifts.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Fecha (vacío = hoy)</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="route-date" /></div>
                <div><Label className="text-xs">Flexibilidad</Label>
                  <Select value={form.flexibility} onValueChange={(v) => setForm({ ...form, flexibility: v })}>
                    <SelectTrigger data-testid="route-flexibility"><SelectValue /></SelectTrigger>
                    <SelectContent>{(meta.route_flexibility || []).map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent></Select></div>
              </div>
              <div><Label className="text-xs">Observaciones</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="route-notes" /></div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setForm(null)}>Cancelar</Button><Button onClick={save} data-testid="save-route-btn">Guardar</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function RouteDetailDialog({ rid, meta, manage, onClose }) {
  const { user } = useAuth();
  const [route, setRoute] = useState(null);
  const [taskForm, setTaskForm] = useState(null);
  const [problemFor, setProblemFor] = useState(null);
  const [evidenceFor, setEvidenceFor] = useState(null);
  const [rescheduleFor, setRescheduleFor] = useState(null);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem("selec_route_view") || "detailed");
  const changeView = (v) => { setViewMode(v); localStorage.setItem("selec_route_view", v); };
  const load = useCallback(() => { api.get(`/routes/${rid}`).then((r) => setRoute(r.data)).catch(() => {}); }, [rid]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (t, status, extra = {}) => {
    try { await api.patch(`/route-tasks/${t.id}/status`, { status, ...extra }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggleDone = (t) => setStatus(t, t.status === "Realizada" ? "Pendiente" : "Realizada");
  const move = async (idx, dir) => {
    const tasks = [...route.tasks]; const j = idx + dir; if (j < 0 || j >= tasks.length) return;
    if (!manage && (tasks[idx].fixed || tasks[j].fixed)) { toast.error("Las tareas fijas solo las reordena quien asignó la ruta."); return; }
    [tasks[idx], tasks[j]] = [tasks[j], tasks[idx]];
    try { await api.post(`/routes/${rid}/reorder`, { task_ids: tasks.map((t) => t.id) }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const saveTask = async () => {
    try {
      if (taskForm.id) await api.put(`/route-tasks/${taskForm.id}`, taskForm);
      else await api.post(`/routes/${rid}/tasks`, taskForm);
      toast.success("Tarea guardada"); setTaskForm(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delTask = async (t) => { if (!window.confirm("¿Eliminar tarea?")) return; try { await api.delete(`/route-tasks/${t.id}`); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const confirmReceived = async () => { try { await api.post(`/routes/${rid}/confirm-received`); toast.success("Ruta confirmada como recibida"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const publishRoute = async () => { try { const r = await api.post(`/routes/${rid}/publish`); toast.success(r.data.message || "Ruta publicada"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="route-detail-dialog">
        {!route ? <div className="py-16"><DialogHeader className="sr-only"><DialogTitle>Cargando ruta</DialogTitle></DialogHeader><div className="flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div></div> : (
          <>
            <DialogHeader><DialogTitle className="flex items-center gap-2">
              <span className="font-mono text-primary">{route.number}</span> {route.name} · {route.shift}
              <Badge className={STATUS_STYLE[route.status] || "bg-slate-100"}>{route.status}</Badge>
            </DialogTitle></DialogHeader>
            <p className="text-sm text-muted-foreground -mt-2">{route.messenger_name} · {route.date} · asignada por {route.assigned_by_name}</p>
            {manage && !route.published && (
              <div className="rounded-md border-2 border-primary/40 bg-primary/5 p-3 space-y-2" data-testid="route-draft-box">
                <p className="text-xs text-muted-foreground flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> <strong>Borrador.</strong> Agrega las tareas y luego publica la ruta para enviársela al mensajero.</p>
                <Button className="gap-1.5 w-full sm:w-auto font-bold" onClick={publishRoute} data-testid="route-publish-btn"><Send className="h-4 w-4" /> Publicar ruta</Button>
              </div>
            )}
            {route.published && route.received_at
              ? <div className="rounded-md border border-green-300 bg-green-50 px-3 py-2 flex items-center gap-2 text-sm text-green-800 font-semibold" data-testid="route-received-stamp"><CheckCircle2 className="h-4 w-4 shrink-0" /> Recibida por {route.received_by_name} · {fmtDateTime(route.received_at)}</div>
              : route.published && user?.id === route.messenger_id
                ? <Button className="gap-1.5 bg-green-600 hover:bg-green-700 w-full sm:w-auto" onClick={confirmReceived} data-testid="route-confirm-received"><CheckCircle2 className="h-4 w-4" /> Confirmar ruta recibida</Button>
                : route.published
                  ? <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs text-amber-800 flex items-center gap-1.5" data-testid="route-received-pending"><Clock className="h-3.5 w-3.5" /> Pendiente de confirmación del mensajero</div>
                  : null}
            <div className="flex justify-between items-center gap-2 flex-wrap">
              <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{route.tasks.length} tareas</span>
              <div className="flex items-center gap-2 ml-auto">
                <div className="flex rounded-lg border p-0.5" data-testid="route-view-toggle">
                  <button onClick={() => changeView("detailed")} className={`px-2.5 py-1 text-xs rounded-md font-semibold transition-colors ${viewMode === "detailed" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`} data-testid="view-detailed">Detallada</button>
                  <button onClick={() => changeView("list")} className={`px-2.5 py-1 text-xs rounded-md font-semibold transition-colors ${viewMode === "list" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`} data-testid="view-list">Lista</button>
                </div>
                {manage && <Button size="sm" className="gap-1.5" onClick={() => setTaskForm({ place: "", client_id: "", party_type: "", party_id: "", address: "", map_url: "", action_type: "Entrega", contact_name: "", phone: "", whatsapp: "", schedule: "", documents: "", notes: "", files: [], fixed: false })} data-testid="add-task-btn"><Plus className="h-4 w-4" /> Tarea</Button>}
              </div>
            </div>
            <div className="space-y-2">
              {viewMode === "list" ? route.tasks.map((t) => (
                <Card key={t.id} className="p-2.5 flex items-center gap-3" data-testid={`task-list-${t.order}`}>
                  <button onClick={() => toggleDone(t)} className={`h-8 w-8 shrink-0 rounded-md border-2 flex items-center justify-center transition-colors ${t.status === "Realizada" ? "bg-green-600 border-green-600 text-white" : "border-muted-foreground/40 hover:border-primary"}`} data-testid={`task-check-${t.order}`}>
                    {t.status === "Realizada" && <CheckCircle2 className="h-5 w-5" />}
                  </button>
                  <span className="font-mono font-bold text-xs w-5 shrink-0">{t.order}</span>
                  <div className="flex-1 min-w-0">
                    <p className={`font-heading font-bold text-sm truncate ${t.status === "Realizada" ? "line-through text-muted-foreground" : ""}`}>{t.place}</p>
                    {t.address && <p className="text-xs text-muted-foreground truncate">{t.address}</p>}
                  </div>
                  <Badge className={`${TYPE_STYLE[t.action_type]} shrink-0`}>{t.action_type}</Badge>
                  {t.fixed && <Badge className="bg-orange-100 text-orange-700 shrink-0 gap-0.5" data-testid={`task-list-fija-${t.order}`}><Lock className="h-3 w-3" />Fija</Badge>}
                  {t.status === "Problema" && <AlertTriangle className="h-4 w-4 text-red-600 shrink-0" />}
                  {(t.map_url || t.address) && <a href={t.map_url || wazeLink(t.address)} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}><Button size="icon" variant="ghost" className="h-8 w-8 shrink-0" data-testid={`task-list-waze-${t.order}`}><Navigation className="h-4 w-4" /></Button></a>}
                  {manage && <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0 text-destructive" title="Quitar de la ruta" onClick={(e) => { e.stopPropagation(); delTask(t); }} data-testid={`task-list-del-${t.order}`}><Trash2 className="h-4 w-4" /></Button>}
                </Card>
              )) : route.tasks.map((t, idx) => (
                <Card key={t.id} className="p-3" data-testid={`task-${t.order}`}>
                  <div className="flex items-start gap-3">
                    <div className="flex flex-col items-center gap-0.5">
                      <span className="h-7 w-7 rounded-lg bg-muted flex items-center justify-center font-mono font-bold text-xs">{t.order}</span>
                      {(manage || !t.fixed) && <>
                        <button onClick={() => move(idx, -1)} className="text-muted-foreground hover:text-foreground" data-testid={`task-up-${t.order}`}><ChevronUp className="h-4 w-4" /></button>
                        <button onClick={() => move(idx, 1)} className="text-muted-foreground hover:text-foreground" data-testid={`task-down-${t.order}`}><ChevronDown className="h-4 w-4" /></button>
                      </>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-heading font-bold">{t.place}</span>
                        <Badge className={TYPE_STYLE[t.action_type]}>{t.action_type}</Badge>
                        <Badge className={STATUS_STYLE[t.status]} data-testid={`task-status-badge-${t.order}`}>{t.status}</Badge>
                        {t.fixed && <Badge className="bg-orange-100 text-orange-700 gap-0.5" data-testid={`task-fija-${t.order}`}><Lock className="h-3 w-3" />Fija</Badge>}
                        {t.status === "Realizada" && <CheckCircle2 className="h-4 w-4 text-green-600" />}
                      </div>
                      {t.address && <p className="text-sm text-muted-foreground flex items-center gap-1 mt-0.5"><MapPin className="h-3 w-3" /> {t.address}</p>}
                      {(t.contact_name || t.phone) && <p className="text-xs text-muted-foreground">{t.contact_name} {t.phone}</p>}
                      {t.schedule && <p className="text-xs text-muted-foreground flex items-center gap-1"><Clock className="h-3 w-3" /> {t.schedule}</p>}
                      {t.documents && <p className="text-xs text-muted-foreground">Docs: {t.documents}</p>}
                      {t.notes && <p className="text-xs italic text-muted-foreground">{t.notes}</p>}
                      {t.problem?.description && <p className="text-xs text-red-600 mt-1">⚠ {t.problem.type}: {t.problem.description}</p>}
                      {(t.files?.length > 0 || t.evidence?.received_by) && (
                        <div className="text-xs text-green-700 mt-1">
                          {t.evidence?.received_by && <span>Recibió: {t.evidence.received_by}. </span>}
                          {(t.files || []).map((fid) => <a key={fid} href={fileUrl(fid)} target="_blank" rel="noreferrer" className="underline mr-2">Ver evidencia</a>)}
                        </div>
                      )}
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {t.map_url
                          ? <a href={t.map_url} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" className="h-7 gap-1 text-xs" data-testid={`task-map-${t.order}`}><Navigation className="h-3.5 w-3.5" /> Cómo llegar</Button></a>
                          : <>
                              {t.address && <a href={wazeLink(t.address)} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" className="h-7 gap-1 text-xs" data-testid={`task-waze-${t.order}`}><Navigation className="h-3.5 w-3.5" /> Waze</Button></a>}
                              {t.address && <a href={mapsLink(t.address)} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" className="h-7 gap-1 text-xs" data-testid={`task-maps-${t.order}`}><MapPin className="h-3.5 w-3.5" /> Maps</Button></a>}
                            </>}
                        {t.whatsapp && <a href={waLink(t.whatsapp)} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" className="h-7 gap-1 text-xs" data-testid={`task-wa-${t.order}`}><MessageCircle className="h-3.5 w-3.5" /> WhatsApp</Button></a>}
                      </div>
                      <div className="flex flex-wrap gap-1.5 mt-1.5">
                        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setStatus(t, "En camino")} data-testid={`task-encamino-${t.order}`}><Truck className="h-3.5 w-3.5 mr-1" />En camino</Button>
                        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setStatus(t, "En proceso")} data-testid={`task-enproceso-${t.order}`}>En proceso</Button>
                        <Button size="sm" variant="ghost" className="h-7 text-xs text-green-700" onClick={() => setEvidenceFor(t)} data-testid={`task-realizar-${t.order}`}><PackageCheck className="h-3.5 w-3.5 mr-1" />Realizar</Button>
                        <Button size="sm" variant="ghost" className="h-7 text-xs text-red-600" onClick={() => setProblemFor(t)} data-testid={`task-problema-${t.order}`}><AlertTriangle className="h-3.5 w-3.5 mr-1" />Problema</Button>
                        {manage && <><Button size="icon" variant="ghost" className="h-7 w-7 ml-auto" title="Reprogramar para otro día" onClick={() => setRescheduleFor(t)} data-testid={`task-reschedule-${t.order}`}><CalendarDays className="h-3.5 w-3.5" /></Button><Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setTaskForm({ id: t.id, place: t.place, client_id: t.client_id || "", party_type: t.party_type || (t.client_id ? "client" : ""), party_id: t.party_id || t.client_id || "", address: t.address, map_url: t.map_url || "", action_type: t.action_type, contact_name: t.contact_name, phone: t.phone, whatsapp: t.whatsapp, schedule: t.schedule, documents: t.documents, notes: t.notes, files: t.files || [], fixed: t.fixed || false })}><Pencil className="h-3.5 w-3.5" /></Button>
                          <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" title="Quitar de la ruta" onClick={() => delTask(t)} data-testid={`task-del-${t.order}`}><Trash2 className="h-3.5 w-3.5" /></Button></>}
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
              {route.tasks.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center">Sin tareas.</p>}
            </div>
          </>
        )}
      </DialogContent>
      {taskForm && <TaskFormDialog form={taskForm} setForm={setTaskForm} meta={meta} onSave={saveTask} routeFlexibility={route?.flexibility} />}
      {problemFor && <ProblemDialog task={problemFor} meta={meta} onClose={() => setProblemFor(null)} onSave={(prob) => { setStatus(problemFor, "Problema", { problem: prob }); setProblemFor(null); }} />}
      {evidenceFor && <EvidenceDialog task={evidenceFor} onClose={() => setEvidenceFor(null)} onDone={() => { setEvidenceFor(null); load(); }} />}
      {rescheduleFor && <RescheduleDialog task={rescheduleFor} onClose={() => setRescheduleFor(null)} onDone={() => { setRescheduleFor(null); load(); }} />}
    </Dialog>
  );
}

function TaskFormDialog({ form, setForm, meta, onSave, routeFlexibility }) {
  const [clients, setClients] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [banks, setBanks] = useState([]);
  const [pq, setPq] = useState("");
  const [pOpen, setPOpen] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [fileNames, setFileNames] = useState({});
  const [uploading, setUploading] = useState(false);
  const parseSlots = (sch) => {
    const s = (sch || "").trim();
    if (!s) return [{ f: "", t: "" }];
    const arr = s.split(",").map((p) => p.trim()).filter(Boolean).map((p) => {
      const m = p.match(/(\d{1,2}:\d{2})\s*-\s*(\d{1,2}:\d{2})/);
      if (m) return { f: m[1], t: m[2] };
      const one = p.match(/(\d{1,2}:\d{2})/);
      return { f: one ? one[1] : "", t: "" };
    });
    return arr.length ? arr : [{ f: "", t: "" }];
  };
  const [slots, setSlots] = useState(() => parseSlots(form.schedule));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setSlots(parseSlots(form.schedule)); }, [form.id]);
  useEffect(() => {
    api.get("/clients?selectable=true").then((r) => setClients(r.data)).catch(() => {});
    api.get("/suppliers").then((r) => setSuppliers(r.data)).catch(() => {});
    api.get("/finance/accounts").then((r) => setBanks(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    if (form.client_id) api.get(`/clients/${form.client_id}`).then((r) => setContacts(r.data.contacts || [])).catch(() => setContacts([]));
    else setContacts([]);
  }, [form.client_id]);

  const deliveryAddr = (c) => c.delivery_address || (c.delivery_addresses || []).find((a) => a.is_primary)?.address || (c.delivery_addresses || [])[0]?.address || c.fiscal_address || "";
  const serializeSlots = (arr) => arr.map((s) => (s.f && s.t ? `${s.f} - ${s.t}` : (s.f || s.t || ""))).filter(Boolean).join(", ");
  const commitSlots = (arr) => { setSlots(arr); setForm({ ...form, schedule: serializeSlots(arr) }); };
  const setSlot = (i, patch) => commitSlots(slots.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  const addSlot = () => commitSlots([...slots, { f: "", t: "" }]);
  const rmSlot = (i) => { const arr = slots.filter((_, idx) => idx !== i); commitSlots(arr.length ? arr : [{ f: "", t: "" }]); };
  const partyOptions = [
    ...clients.map((c) => ({ type: "client", id: c.id, label: c.business_name, search: c.commercial_name || "", tag: "Cliente", address: deliveryAddr(c), map_url: c.map_url || "" })),
    ...suppliers.map((s) => ({ type: "supplier", id: s.id, label: s.company, tag: "Proveedor", address: s.address || "", map_url: s.map_url || "" })),
    ...banks.map((b) => ({ type: "bank", id: b.id, label: b.name || b.bank || "Banco", tag: "Banco", address: "", map_url: "" })),
    ...GT_BANKS.map((name) => ({ type: "bank", id: `gtbank:${name}`, label: name, tag: "Banco", address: "", map_url: "" })),
  ];
  const selectedParty = partyOptions.find((o) => o.type === (form.party_type || (form.client_id ? "client" : "")) && o.id === (form.party_id || form.client_id));
  const nrm = (s) => (s || "").toString().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const filteredParties = (pq.trim() ? partyOptions.filter((o) => nrm(o.label).includes(nrm(pq)) || nrm(o.search || "").includes(nrm(pq)) || nrm(o.tag).includes(nrm(pq))) : partyOptions).slice(0, 40);
  const selectParty = (o) => { setForm({ ...form, party_type: o.type, party_id: o.id, client_id: o.type === "client" ? o.id : "", place: o.label, address: o.address || form.address, map_url: o.map_url || form.map_url }); setPq(""); setPOpen(false); };
  const clearParty = () => setForm({ ...form, party_type: "", party_id: "", client_id: "" });
  const pickContact = (v) => {
    if (v === "none") return;
    const c = contacts.find((x) => x.id === v); if (!c) return;
    const nm = `${c.name || ""}${c.last_name ? " " + c.last_name : ""}`.trim();
    const ph = c.mobile || c.phone || "";
    setForm({ ...form, contact_name: nm, phone: ph, whatsapp: c.whatsapp || ph });
  };
  const upload = async (e) => {
    const f = e.target.files[0]; if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((p) => ({ ...p, files: [...(p.files || []), data.id] }));
      setFileNames((n) => ({ ...n, [data.id]: data.original_filename }));
    } catch (err) { toast.error("Error al subir archivo"); } finally { setUploading(false); }
  };
  const removeFile = (id) => setForm({ ...form, files: (form.files || []).filter((x) => x !== id) });
  const onDocAttached = (data, number) => {
    setForm((p) => {
      const docs = (p.documents || "").trim();
      return { ...p, files: [...(p.files || []), data.id], documents: number && !docs.includes(number) ? (docs ? `${docs}, ${number}` : number) : p.documents };
    });
    setFileNames((n) => ({ ...n, [data.id]: data.original_filename }));
  };

  return (
    <Dialog open onOpenChange={() => setForm(null)}>
      <DialogContent data-testid="task-form-dialog">
        <DialogHeader><DialogTitle>{form.id ? "Editar tarea" : "Nueva tarea"}</DialogTitle></DialogHeader>
        <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-1">
          <div><Label className="text-xs">Cliente, proveedor o banco (opcional)</Label>
            <div className="relative">
              <Input value={selectedParty ? `${selectedParty.tag}: ${selectedParty.label}` : pq}
                onChange={(e) => { if (selectedParty) clearParty(); setPq(e.target.value); setPOpen(true); }}
                onFocus={() => setPOpen(true)}
                onBlur={() => setTimeout(() => setPOpen(false), 150)}
                placeholder="Escribe para buscar cliente, proveedor o banco…" data-testid="task-party-search" />
              {pOpen && (
                <div className="absolute z-50 mt-1 w-full max-h-56 overflow-y-auto rounded-md border border-border bg-popover shadow-md" data-testid="task-party-list">
                  {filteredParties.length === 0 && <p className="px-3 py-2 text-xs text-muted-foreground">Sin resultados</p>}
                  {filteredParties.map((o) => (
                    <button type="button" key={o.type + o.id} onMouseDown={(e) => { e.preventDefault(); selectParty(o); }} data-testid={`task-party-opt-${o.type}-${o.id}`}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-muted flex items-center justify-between gap-2">
                      <span className="truncate">{o.label}{o.search ? <span className="text-muted-foreground"> · {o.search}</span> : ""}</span>
                      <span className="text-[10px] uppercase tracking-widest text-muted-foreground shrink-0">{o.tag}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div><Label className="text-xs">Lugar / Empresa</Label><Input value={form.place} onChange={(e) => setForm({ ...form, place: e.target.value })} data-testid="task-place" /></div>
          <div><Label className="text-xs">Tipo</Label>
            <Select value={form.action_type} onValueChange={(v) => setForm({ ...form, action_type: v })}>
              <SelectTrigger data-testid="task-type"><SelectValue /></SelectTrigger>
              <SelectContent>{meta.task_types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
          <div><Label className="text-xs">Horario(s) — puedes dejar uno o agregar varios (ej. 08:00-13:00 y 14:00-17:00)</Label>
            <div className="space-y-1.5">
              {slots.map((s, i) => (
                <div key={i} className="flex items-center gap-1.5" data-testid={`task-slot-${i}`}>
                  <Input type="time" value={s.f} onChange={(e) => setSlot(i, { f: e.target.value })} className="flex-1" data-testid={`task-time-from-${i}`} />
                  <span className="text-xs text-muted-foreground">a</span>
                  <Input type="time" value={s.t} onChange={(e) => setSlot(i, { t: e.target.value })} className="flex-1" data-testid={`task-time-to-${i}`} />
                  {slots.length > 1 && <Button type="button" size="icon" variant="ghost" className="h-8 w-8 shrink-0 text-destructive" onClick={() => rmSlot(i)} data-testid={`task-slot-rm-${i}`}><X className="h-3.5 w-3.5" /></Button>}
                </div>
              ))}
              <Button type="button" size="sm" variant="outline" className="gap-1 text-xs h-7" onClick={addSlot} data-testid="task-slot-add"><Plus className="h-3.5 w-3.5" /> Agregar horario</Button>
            </div></div>
          {routeFlexibility === "Semiflexible" && (
            <div><Label className="text-xs">Prioridad en la ruta (semiflexible)</Label>
              <Select value={form.fixed ? "fija" : "flexible"} onValueChange={(v) => setForm({ ...form, fixed: v === "fija" })}>
                <SelectTrigger data-testid="task-fixed"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="flexible">Flexible · se puede reordenar</SelectItem>
                  <SelectItem value="fija">Fija · respetar horario y orden</SelectItem>
                </SelectContent></Select></div>
          )}
          <div><Label className="text-xs">Dirección de entrega</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="task-address" /></div>
          <div><Label className="text-xs">Ubicación (link Waze / Google Maps)</Label><Input value={form.map_url || ""} onChange={(e) => setForm({ ...form, map_url: e.target.value })} placeholder="Pega el link de Waze o Google Maps" data-testid="task-map-url" /></div>
          {form.client_id && contacts.length > 0 && (
            <div><Label className="text-xs">Elegir contacto de la empresa</Label>
              <Select value="none" onValueChange={pickContact}>
                <SelectTrigger data-testid="task-contact-pick"><SelectValue placeholder="Seleccionar contacto" /></SelectTrigger>
                <SelectContent><SelectItem value="none">— Escribir manual —</SelectItem>{contacts.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} {c.last_name} {c.position ? `· ${c.position}` : ""}</SelectItem>)}</SelectContent></Select></div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Contacto</Label><Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} data-testid="task-contact" /></div>
            <div><Label className="text-xs">Teléfono</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="task-phone" /></div>
          </div>
          <div><Label className="text-xs">Documentos a entregar (referencia)</Label><Input value={form.documents} onChange={(e) => setForm({ ...form, documents: e.target.value })} placeholder="Factura FAC-0001, Nota EHC-01..." data-testid="task-docs" /></div>
          <div>
            <Label className="text-xs">Adjuntar archivos (fotos, órdenes de compra, facturas, envíos…)</Label>
            <label className="flex items-center justify-center gap-2 cursor-pointer border border-dashed rounded-xl p-3 text-sm font-semibold text-primary hover:bg-primary/5 mt-1" data-testid="task-file-label">
              {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Plus className="h-4 w-4" />} Adjuntar archivo
              <input type="file" className="hidden" accept="image/*,application/pdf" onChange={upload} data-testid="task-file-input" />
            </label>
            <TaskDocAttach clientId={form.client_id} onAttached={onDocAttached} />
            <div className="flex flex-wrap gap-2 mt-2">
              {(form.files || []).map((id, i) => (
                <span key={id} className="text-xs bg-muted px-2 py-1 rounded-lg flex items-center gap-1" data-testid="task-file-chip">
                  <a href={fileUrl(id)} target="_blank" rel="noreferrer" className="underline">{fileNames[id] || `Archivo ${i + 1}`}</a>
                  <X className="h-3 w-3 cursor-pointer" onClick={() => removeFile(id)} />
                </span>
              ))}
            </div>
          </div>
          <div><Label className="text-xs">Detalles / observaciones</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="task-notes" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => setForm(null)}>Cancelar</Button><Button onClick={onSave} disabled={!form.place} data-testid="save-task-btn">Guardar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ProblemDialog({ task, meta, onClose, onSave }) {
  const [p, setP] = useState({ type: meta.problem_types[0] || "Otro", description: "", person: "", new_date: "", needs_reassign: false });
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="problem-dialog">
        <DialogHeader><DialogTitle>Reportar problema · #{task.order}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Tipo</Label>
            <Select value={p.type} onValueChange={(v) => setP({ ...p, type: v })}>
              <SelectTrigger data-testid="problem-type"><SelectValue /></SelectTrigger>
              <SelectContent>{meta.problem_types.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent></Select></div>
          <div><Label className="text-xs">Descripción</Label><Textarea value={p.description} onChange={(e) => setP({ ...p, description: e.target.value })} data-testid="problem-desc" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Persona con quien habló</Label><Input value={p.person} onChange={(e) => setP({ ...p, person: e.target.value })} data-testid="problem-person" /></div>
            <div><Label className="text-xs">Nueva fecha/acción</Label><Input value={p.new_date} onChange={(e) => setP({ ...p, new_date: e.target.value })} data-testid="problem-newdate" /></div>
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancelar</Button><Button className="bg-red-600 hover:bg-red-700" onClick={() => onSave(p)} data-testid="save-problem-btn">Reportar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EvidenceDialog({ task, onClose, onDone }) {
  const [ev, setEv] = useState({ received_by: "", comments: "", amount: "", payment_method: "", receipt_number: "" });
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const isCobro = task.action_type === "Cobro";

  const upload = async (e) => {
    const f = e.target.files[0]; if (!f) return;
    setUploading(true);
    try { const fd = new FormData(); fd.append("file", f); const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); setFiles((x) => [...x, data]); }
    catch (err) { toast.error("Error al subir archivo"); } finally { setUploading(false); }
  };
  const save = async () => {
    try {
      const payload = { received_by: ev.received_by, comments: ev.comments, payment_method: ev.payment_method, receipt_number: ev.receipt_number, file_ids: files.map((f) => f.id) };
      if (isCobro && ev.amount) payload.amount = Number(ev.amount);
      if (files.length || ev.received_by || ev.amount) await api.post(`/route-tasks/${task.id}/evidence`, payload);
      await api.patch(`/route-tasks/${task.id}/status`, { status: "Realizada" });
      toast.success("Tarea realizada"); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="evidence-dialog">
        <DialogHeader><DialogTitle>Evidencia · #{task.order} {task.place}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Recibido por</Label><Input value={ev.received_by} onChange={(e) => setEv({ ...ev, received_by: e.target.value })} data-testid="ev-received" /></div>
          {isCobro && (
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Monto cobrado</Label><Input type="number" value={ev.amount} onChange={(e) => setEv({ ...ev, amount: e.target.value })} data-testid="ev-amount" /></div>
              <div><Label className="text-xs">Forma de pago</Label><Input value={ev.payment_method} onChange={(e) => setEv({ ...ev, payment_method: e.target.value })} data-testid="ev-method" /></div>
              <div className="col-span-2"><Label className="text-xs"># Recibo</Label><Input value={ev.receipt_number} onChange={(e) => setEv({ ...ev, receipt_number: e.target.value })} data-testid="ev-receipt" /></div>
            </div>
          )}
          <div><Label className="text-xs">Comentarios</Label><Textarea value={ev.comments} onChange={(e) => setEv({ ...ev, comments: e.target.value })} data-testid="ev-comments" /></div>
          <div>
            <Label className="text-xs">Fotos / documentos</Label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              <label className="flex items-center justify-center gap-2 cursor-pointer border border-dashed rounded-xl p-3 text-sm font-semibold text-primary hover:bg-primary/5 min-h-[52px]" data-testid="ev-camera-label">
                {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Camera className="h-5 w-5" />} Tomar foto
                <input type="file" className="hidden" accept="image/*" capture="environment" onChange={upload} data-testid="ev-camera-input" />
              </label>
              <label className="flex items-center justify-center gap-2 cursor-pointer border border-dashed rounded-xl p-3 text-sm text-muted-foreground hover:bg-muted min-h-[52px]" data-testid="ev-upload-label">
                {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <X className="h-4 w-4 rotate-45" />} Adjuntar archivo
                <input type="file" className="hidden" accept="image/*,application/pdf" onChange={upload} data-testid="ev-file-input" />
              </label>
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {files.map((f) => <span key={f.id} className="text-xs bg-muted px-2 py-1 rounded-lg flex items-center gap-1" data-testid="ev-file-chip">{f.original_filename}<X className="h-3 w-3 cursor-pointer" onClick={() => setFiles(files.filter((x) => x.id !== f.id))} /></span>)}
            </div>
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancelar</Button><Button className="bg-green-600 hover:bg-green-700" onClick={save} data-testid="save-evidence-btn">Marcar realizada</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function RescheduleDialog({ task, onClose, onDone }) {
  const [date, setDate] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!date) return toast.error("Selecciona la fecha");
    const [y, m, d] = date.split("-");
    setSaving(true);
    try {
      await api.post(`/route-tasks/${task.id}/reschedule`, { date: `${d}/${m}/${y}` });
      toast.success("Actividad reprogramada");
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="reschedule-dialog">
        <DialogHeader><DialogTitle>Reprogramar actividad</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">"{task.place}" se moverá a la ruta del mensajero para el día que elijas. Si ese día no tiene ruta, se crea automáticamente.</p>
          <div><Label className="text-xs">Nueva fecha</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="reschedule-date" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancelar</Button><Button onClick={submit} disabled={saving} data-testid="reschedule-save">Reprogramar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
